import React from 'react'
import Panel from '@components/common/Panel'
import FlexContainer from '@components/common/FlexContainer'
import Checkbox from '@components/common/form/Checkbox'
import LabelInfo from '@components/common/form/LabelInfo'
import Input from '@components/common/form/Input'
import Dropdown from '@components/common/form/Dropdown'
import { Button, TimeInput } from '@components/common/form'
import YSpacing from '@components/common/YSpacing'
import { Moment } from 'moment-timezone'
import {
  ScheduleSettings,
  CutoffTimeSettings,
  CutoffTimeDayOffSetType,
  DropoffTimeSettings,
} from '@types'

const CUTOFF_DAY_OFFSET_TYPES = [
  { value: CutoffTimeDayOffSetType.BUSINESS_DAYS, label: 'Business Days' },
  { value: CutoffTimeDayOffSetType.DAYS, label: 'Days - Includes Weekends' },
]

interface Props {
  scheduleSettings: ScheduleSettings | null
  onUpdate: (settings: ScheduleSettings) => void
  onSave: () => Promise<void>
  onCreate: () => ScheduleSettings
}

const UniversalTimesPanel: React.FC<Props> = ({
  scheduleSettings,
  onUpdate,
  onSave,
  onCreate,
}) => {
  const updateCutoffTimeSettings = (
    key: string,
    value: number | Moment | string,
  ) => {
    if (!scheduleSettings) {
      return
    }
    onUpdate({
      ...scheduleSettings,
      cutoffTimeSettings: {
        ...scheduleSettings.cutoffTimeSettings,
        [key]: value,
      } as CutoffTimeSettings,
    })
  }

  const updateDropoffTime = (value: Moment) => {
    if (!scheduleSettings) {
      return
    }
    onUpdate({
      ...scheduleSettings,
      dropoffTimeSettings: {
        ...scheduleSettings.dropoffTimeSettings,
        time: value,
      } as DropoffTimeSettings,
    })
  }

  const updateIsActive = (value: boolean) => {
    if (!scheduleSettings) {
      return
    }
    onUpdate({ ...scheduleSettings, isActive: value })
  }

  return (
    <Panel width="100%" heading="Universal Schedule Settings">
      <LabelInfo label="Global defaults for cutoff and dropoff — inherited by all schedules unless overridden." />
      <YSpacing height="8px" />
      {scheduleSettings ? (
        <>
          <FlexContainer
            width="100%"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Input
              width="20%"
              label="Cutoff Day"
              value={scheduleSettings.cutoffTimeSettings?.dayOffset?.toString()}
              type="number"
              min="0"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateCutoffTimeSettings('dayOffset', e.target.valueAsNumber)
              }
            />
            <Dropdown
              width="75%"
              value={scheduleSettings.cutoffTimeSettings?.dayOffsetType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateCutoffTimeSettings('dayOffsetType', e.target.value)
              }
            >
              {CUTOFF_DAY_OFFSET_TYPES.map((r) => (
                <option
                  key={r.value}
                  value={r.value}
                  selected={
                    r.value ===
                    scheduleSettings.cutoffTimeSettings?.dayOffsetType
                  }
                >
                  {r.label}
                </option>
              ))}
            </Dropdown>
          </FlexContainer>
          <TimeInput
            width="100%"
            label="Cutoff Time"
            time={scheduleSettings.cutoffTimeSettings?.time}
            testId="universal-cutoff-time"
            onChange={(value: Moment) =>
              updateCutoffTimeSettings('time', value)
            }
          />
          <YSpacing height="12px" />
          <TimeInput
            width="100%"
            label="Dropoff Time"
            time={scheduleSettings.dropoffTimeSettings?.time}
            testId="universal-dropoff-time"
            onChange={updateDropoffTime}
          />
          <YSpacing height="12px" />
          <Checkbox
            label="Active?"
            value={'checkbox'}
            checked={scheduleSettings.isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateIsActive(e.target.checked)
            }
          />
          <YSpacing height="12px" />
          <Button size="sm" label="Save Changes" onClick={onSave} />
        </>
      ) : (
        <Checkbox
          label="Set Custom Default Cutoff Time"
          value={'cutoff-time-checkbox'}
          checked={false}
          onChange={() => {
            onUpdate(onCreate())
          }}
        />
      )}
    </Panel>
  )
}

export default UniversalTimesPanel
